// templateEngine.js — variable system, storage helpers, sample data

// ── All supported variables ──────────────────────────────────────────────

export const VARIABLES = [
  { key: 'student_name',  label: 'Student Name'    },
  { key: 'father_name',   label: 'Father Name'     },
  { key: 'mother_name',   label: 'Mother Name'     },
  { key: 'admission_no',  label: 'Admission No'    },
  { key: 'dob',           label: 'Date of Birth'   },
  { key: 'class_name',    label: 'Class'           },
  { key: 'section_name',  label: 'Section'         },
  { key: 'academic_year', label: 'Academic Year'   },
  { key: 'date',          label: 'Issue Date'      },
  { key: 'tc_no',         label: 'TC Number'       },
  { key: 'serial_no',     label: 'Serial No'       },
  { key: 'conduct',       label: 'Conduct'         },
  { key: 'religion',      label: 'Religion'        },
  { key: 'nationality',   label: 'Nationality'     },
  { key: 'category',      label: 'Category'        },
  { key: 'pen_no',        label: 'PEN No'          },
  { key: 'aadhar_no',     label: 'Aadhar No'       },
  { key: 'medium',        label: 'Medium'          },
  { key: 'mother_tongue', label: 'Mother Tongue'   },
  { key: 'first_lang',    label: 'First Language'  },
  { key: 'second_lang',   label: 'Second Language' },
  { key: 'phone',         label: 'Phone'           },
  { key: 'school_code',   label: 'UDISE No.'       },
  { key: 'exam_result',   label: 'Exam Result'     },
  { key: 'promoted_class',label: 'Promoted Class'  },
  { key: 'leaving_date',  label: 'Date of Leaving' },
  { key: 'reason',        label: 'Reason'          },
  { key: 'remarks',       label: 'Remarks'         },
  { key: 'blood_group',      label: 'Blood Group'           },
  { key: 'student_address',  label: 'Student Address'       },
  { key: 'student_photo',    label: 'Student Photo URL'     },
  { key: 'promotion_status', label: 'Promotion Status'      },
  { key: 'admission_date',   label: 'Date of Admission'     },
  { key: 'working_days',     label: 'Working Days / Days Present' },
];

// ── Variable label lookup map ─────────────────────────────────────────────

export const VARIABLE_LABEL_MAP = Object.fromEntries(
  VARIABLES.map(v => [v.key, v.label])
);

// ── Sample data (used in live preview) ──────────────────────────────────

export const SAMPLE_DATA = {
  student_name:  'STUDENT FULL NAME',
  father_name:   'FATHER NAME',
  mother_name:   'MOTHER NAME',
  admission_no:  'XXXXX',
  dob:           'DD/MM/YYYY',
  class_name:    'CLASS',
  section_name:  'SECTION',
  academic_year: 'XXXX-XX',
  date:          'DD/MM/YYYY',
  tc_no:         'TC/XXXX/XXX',
  serial_no:     'XXX',
  conduct:       'CONDUCT',
  religion:      'RELIGION',
  nationality:   'NATIONALITY',
  category:      'CATEGORY',
  pen_no:        'XXXXXXXXXX',
  aadhar_no:     'XXXX-XXXX-XXXX',
  medium:        'MEDIUM',
  mother_tongue: 'MOTHER TONGUE',
  first_lang:    'FIRST LANGUAGE',
  second_lang:   'SECOND LANGUAGE',
  phone:         'XXXXXXXXXX',
  school_code:   '36221191912',
  exam_result:   'RESULT',
  promoted_class:'CLASS',
  leaving_date:  'DD/MM/YYYY',
  reason:        'REASON FOR LEAVING',
  remarks:       'REMARKS',
  blood_group:      'B +ve',
  student_address:  'Shreeram Aprt, Shivajinagar, Pune',
  student_photo:    '',
  promotion_status: 'PROMOTED',
  admission_date:   'DD/MM/YYYY',
  working_days:     '220 / 210',
};

// ── Extract unique variable keys used inside an HTML string ──────────────

export const extractVariablesFromHtml = (html) => {
  if (!html) return [];
  const matches = [...html.matchAll(/\{\{([a-zA-Z_]+)\}\}/g)];
  return [...new Set(matches.map(m => m[1]))];
};

// ── Replace {{variable}} tokens in HTML with student data values ──────────
// Unmatched tokens → blank underline placeholder.
// Hidden keys (passed in hiddenKeys array) → empty string (field removed).

// Format an ISO-style date value (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss) to DD/MM/YYYY.
// Any non-date value is returned unchanged.
const formatDisplayDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return value;
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
};

export const replaceVariables = (html, data = {}, hiddenKeys = []) => {
  if (!html) return '';
  let result = html;

  // Hidden fields first — replace with empty string so they vanish cleanly
  hiddenKeys.forEach(key => {
    result = result.replace(
      new RegExp(`\\{\\{${key}\\}\\}`, 'g'),
      ''
    );
  });

  // Provided data — date values are normalised to DD/MM/YYYY for display
  Object.entries(data).forEach(([key, value]) => {
    const out = value == null ? '' : formatDisplayDate(value);
    result = result.replace(
      new RegExp(`\\{\\{${key}\\}\\}`, 'g'),
      out
    );
  });

  // Remaining unmatched → blank underline (shows placeholder in preview)
  result = result.replace(
    /\{\{[a-zA-Z_]+\}\}/g,
    '<span style="display:inline-block;min-width:80px;">&nbsp;</span>'
  );
  
  return result;
};

// ── Local-storage persistence ─────────────────────────────────────────────

const STORAGE_KEY = 'rizee_cert_templates';

export const saveTemplateToStorage = (template) => {
  try {
    const list = loadTemplatesFromStorage();
    const idx  = list.findIndex(t => t.id === template.id);
    if (idx >= 0) list[idx] = template;
    else list.push({ ...template, id: template.id || Date.now() });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
};

export const loadTemplatesFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

export const deleteTemplateFromStorage = (id) => {
  try {
    const list = loadTemplatesFromStorage().filter(t => t.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
};

const DELETED_BUILTINS_KEY = 'rizee_cert_deleted_builtins';

export const loadDeletedBuiltins = () => {
  try { return JSON.parse(localStorage.getItem(DELETED_BUILTINS_KEY) || '[]'); }
  catch { return []; }
};

export const deleteBuiltinFromStorage = (id) => {
  try {
    const list = [...new Set([...loadDeletedBuiltins(), id])];
    localStorage.setItem(DELETED_BUILTINS_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
};

// ── Build field config array from variable keys ───────────────────────────
// Used to initialize cfg.fields when loading a template or changing cert type.

export const buildFieldsFromKeys = (keys) =>
  keys.map(key => ({
    key,
    label: VARIABLE_LABEL_MAP[key] || key.replace(/_/g, ' '),
    visible: true,
  }));
